import React from 'react';
import ReactApexChart from "react-apexcharts";

import { fetchBloodPressureGraphData } from '../../HTTPS/GraphApis';
import { extractChartItemsWithMatchingEventDates } from '../../utils/chartUtils';
import { groupByDateForLineGraph, localisingTimestamp, processGroupedDataForLineGraph } from '../../utils/reuseableFunc';
import useVitalChartQuery from '../../hooks/useVitalChartQuery';
import { usePatient } from '../../context/PatientContext';


function BloodPressureChart({ events, lastValueDate }) {
  const numberOfDaysToFetch = 35;
  const numberOfDaysToShow = 7;
  const { patient } = usePatient();

  const id = "blood-pressure";
  const chartName = patient?.fname + "_" + id

  // calling chart data
  const { data: chartData, isFetched, isLoading } = useVitalChartQuery(
    "bloodPressureGraph",
    fetchBloodPressureGraphData,
    numberOfDaysToFetch
  );

  // Check if chartData is available before rendering the chart
  if (isLoading) {
    return <div className="w-full p-3 bg-black/40 rounded-lg text-center text-white text-lg">
      <p>Loading...</p>
    </div>;
  }

  // Check if chartData is available before rendering the chart
  if (!isFetched || !(chartData?.length > 0)) {
    return <div className="w-full p-3 bg-black/40 rounded-lg text-center text-white text-lg">
      {!lastValueDate && <p>No Data Available for this Vital</p>}
      {!!lastValueDate && <p>No Recent Readings Captured for this Vital</p>}
    </div>;
  }

  const localizedChartData = localisingTimestamp(chartData)

  const groupedByDate = groupByDateForLineGraph(localizedChartData)

  const roundOffedSystolic = processGroupedDataForLineGraph(groupedByDate, 'systolic', numberOfDaysToShow)
  const roundOffedDiastolic = processGroupedDataForLineGraph(groupedByDate, 'diastolic', numberOfDaysToShow)

  return (
    <>
      <div className="w-full mt-10 border-8 border-black/40 rounded-lg">
        <ReactApexChart
          type="line"
          series={[
            {
              name: "Systolic",
              type: "line",
              data: roundOffedSystolic,
            },
            {
              name: "Diastolic",
              type: "line",
              data: roundOffedDiastolic,
            },
            {
              name: "Activities",
              type: 'scatter',
              data: extractChartItemsWithMatchingEventDates(events, roundOffedSystolic),
            },
          ]}
          width="100%"
          height="380"
          options={{
            chart: {
              id,
              parentHeightOffset: 0,
              toolbar: {
                show: true,
                tools: {
                  download: false
                }
              },
              foreColor: "#FAFCFE", // color of the legend's text
              background: 'rgba(0, 0, 0, 0.4)',
            },
            stroke: { // line of the graph setting
              width: 3,
              // curve: "smooth",
              colors: ['#53C5EA', '#FF0000'],
            },
            dataLabels: {
              enabled: false,
            },
            markers: { // this is for dot on the graph
              size: [0, 0, 8],
              strokeWidth: 0,
              colors: '#ED7117ff', // adding alpha values to disable default opacity
            },
            tooltip: { // tooltip setting when we hover over the charts dots
              enabled: false,
            },
            legend: { // this is for name of the graphs at the bottom
              fontFamily: "Poppins, Helvetica, Arial, sans-serif",
              markers: {
                // eslint-disable-next-line no-sparse-arrays
                fillColors: ['#53C5EA', '#FF0000', '#ED7117ff']
              }
            },
            grid: { // bg grid lines of the graph
              borderColor: "rgba(236, 241, 244, 0.4)",
            },
            xaxis: {
              type: "datetime",
              axisTicks: { show: true },
              labels: {
                style: {
                  colors: "#FAFCFE",
                  fontFamily: "Poppins, Helvetica, Arial, sans-serif",
                  fontWeight: 500,
                },
              },
            },
            yaxis: {
              labels: {
                style: {
                  colors: "#FAFCFE",
                  fontFamily: "Poppins, Helvetica, Arial, sans-serif",
                  fontWeight: 500,
                },
              },
            },
          }}
        />
      </div>
    </>
  );
}

export default BloodPressureChart;